import { useEffect, useState } from 'react';

import { usePrevious } from '../../../../utils/hooks/usePrevious';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../api/restaurant/restaurant.types';
import { flagService } from '../../../services/flagService';
import { NutritionInfoDataDictionary } from '../../../state/menu/menu.types';
import { Async, AsyncMap, Status } from '../../../types/data.types';

interface UseNutritionInfo {
  data: NutritionInfoDataDictionary | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  showData: boolean;
}

export const useNutritionInfo = (
  loadNutritionInfo: (url: string) => void,
  nutritionInfos: AsyncMap<NutritionInfoDataDictionary>,
  restaurantDetail: CollectionRestaurantDetail | DeliveryRestaurantDetail, // Get from useRestaurantDetails hook
): UseNutritionInfo => {
  const nutritionInfoUrl = restaurantDetail?.NutritionInfoUrl;
  const previousNutritionInfoUrl = usePrevious(nutritionInfoUrl);

  const isNutritionInfoEnabled = flagService.isFlagOn('nutritionalInformation');

  const getNutritionInfoInitialState =
    (): Async<NutritionInfoDataDictionary> => {
      let initialState: Async<NutritionInfoDataDictionary> = {
        status: Status.LOADING,
      };

      if (nutritionInfoUrl) {
        const nutritionInfoFromRedux = nutritionInfos[nutritionInfoUrl];
        if (nutritionInfoFromRedux?.status === Status.SUCCESS) {
          initialState = nutritionInfoFromRedux;
        }
      }

      return initialState;
    };

  const [nutritionInfo, setNutritionInfo] = useState<
    Async<NutritionInfoDataDictionary>
  >(() => {
    const initialState = getNutritionInfoInitialState();
    return initialState;
  });

  const isError = nutritionInfo.status === Status.ERROR;
  const isLoading =
    isNutritionInfoEnabled &&
    !!nutritionInfoUrl &&
    nutritionInfo.status === Status.LOADING;
  const isSuccess = nutritionInfo.status === Status.SUCCESS;
  const data = isSuccess ? nutritionInfo.data : null;
  const isEmpty =
    isSuccess &&
    Object.keys(data.menuItems).length === 0 &&
    Object.keys(data.menuItemOptionSetItems).length === 0;
  const showData = isNutritionInfoEnabled && !!nutritionInfoUrl && !isEmpty;

  useEffect(() => {
    const isNutritionInfoNotLoaded = !isSuccess && !!nutritionInfoUrl;
    const hasNutritionInfoUpdated =
      previousNutritionInfoUrl && previousNutritionInfoUrl !== nutritionInfoUrl;
    if (
      isNutritionInfoEnabled &&
      (isNutritionInfoNotLoaded || hasNutritionInfoUpdated)
    ) {
      // * Redux state - menu.nutritionInfos[nutritionInfoUrl]
      // - set nutritionInfo indexed by nutritionInfoUrl
      loadNutritionInfo(nutritionInfoUrl);
    }
  }, [nutritionInfoUrl]);

  useEffect(() => {
    const nutritionInfoFromRedux = nutritionInfos[nutritionInfoUrl];
    if (nutritionInfoFromRedux) {
      setNutritionInfo(nutritionInfoFromRedux);
    }
  }, [nutritionInfos[nutritionInfoUrl]?.status, nutritionInfoUrl]);

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    showData,
  };
};
